import "./generated/client-bridge";

import { ClerkProvider } from "@clerk/clerk-react";
import { dark } from "@clerk/ui/themes";
import * as Sentry from "@sentry/react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { initAnalytics } from "./lib/analytics";
import { routeTree } from "./routeTree.gen";
import "./index.css";

// Get Clerk publishable key from environment
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error("Missing Clerk Publishable Key");
}

const isCancel = (error: unknown) =>
  (error as { code?: string })?.code === "ERR_CANCELED" ||
  (error as { name?: string })?.name === "CanceledError";

// Create a QueryClient instance with Sentry error tracking
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (isCancel(error)) return;
      Sentry.captureException(error, {
        tags: {
          errorType: "react-query",
        },
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (isCancel(error)) return;
      Sentry.captureException(error, {
        tags: {
          errorType: "react-query-mutation",
        },
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// Create the router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
});

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENVIRONMENT || "production",
  enabled: import.meta.env.VITE_ENVIRONMENT !== "development",
  integrations: [
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    // TODO: feedback
    Sentry.feedbackIntegration({
      // Additional SDK configuration goes in here, for example:
      colorScheme: "system",
      autoInject: true,
      showBranding: false,
      triggerLabel: "Feedback",
      formTitle: "Give Feedback",
      submitButtonLabel: "Send",
      messagePlaceholder: "Describe your experience…",
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
    }),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1, // 10% of sessions
  replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
});

// Initialize PostHog (product analytics + paid-user session recording).
// Separate from Sentry: Sentry = errors, PostHog = behaviour.
initAnalytics();

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface HistoryState {
    uploadedFile?: File;
    uploadedFiles?: (File | null)[];
    referenceImagePreviews?: string[];
    referenceAssetIds?: (string | null)[];
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element not found");
createRoot(rootEl).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<div>An error has occurred</div>}>
      <ClerkProvider
        publishableKey={CLERK_PUBLISHABLE_KEY}
        signInUrl="/sign-in"
        signUpUrl="/sign-up"
        signInFallbackRedirectUrl="/"
        signUpFallbackRedirectUrl="/"
        appearance={{
          theme: dark,
        }}
      >
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </ClerkProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
